<template>
  <a-drawer
    width="90%"
    :visible="visible"
    :destroy-on-close="true"
    :title="`任务详情（${projectName || '项目'}）`"
    :body-style="{ padding: '16px 16px 20px' }"
    @close="handleClose"
  >
    <div class="task-drawer-body">
      <div class="task-drawer-filters">
        <a-form layout="inline" class="task-filter-form">
          <a-form-item label="时间区间">
            <a-range-picker
              v-model="filters.dateRange"
              :allow-clear="true"
              :disabled-date="disabledDateAfterToday"
              format="YYYY-MM-DD"
              value-format="YYYY-MM-DD"
              placeholder="['开始日期', '结束日期']"
              class="filter-range-picker"
              @change="onDateRangeChange"
            />
          </a-form-item>
          <a-form-item label="人员">
            <a-select
              v-model="filters.userIds"
              mode="multiple"
              allow-clear
              show-search
              placeholder="人员 / 完成率"
              option-filter-prop="title"
              :max-tag-count="2"
              class="filter-user-select"
              @change="onFilterChange"
            >
              <a-select-option
                v-for="u in userOptions"
                :key="u.userId"
                :value="u.userId"
                :title="`${u.nickname} ${formatRate(u.completionRate)}`"
              >
                <span class="user-option">
                  <span class="user-option-name">{{ u.nickname }}</span>
                  <span class="user-option-rate">{{ formatRate(u.completionRate) }}</span>
                </span>
              </a-select-option>
            </a-select>
          </a-form-item>
          <a-form-item label="状态">
            <a-select
              v-model="filters.taskStatus"
              placeholder="全部"
              allow-clear
              class="filter-status-select"
              @change="onFilterChange"
            >
              <a-select-option :value="3">待定</a-select-option>
              <a-select-option :value="0">未开始</a-select-option>
              <a-select-option :value="1">进行中</a-select-option>
              <a-select-option :value="2">已完成</a-select-option>
            </a-select>
          </a-form-item>
          <span class="task-filter-btns">
            <a-button type="primary" @click="handleQuery">查询</a-button>
            <a-button @click="resetQuery">重置</a-button>
            <a-button type="primary" ghost :loading="exportLoading" @click="handleExport">导出</a-button>
          </span>
        </a-form>
      </div>
      <a-table
        :loading="loading"
        :columns="columns"
        :data-source="list"
        :pagination="false"
        row-key="id"
        :scroll="{ x: 'max-content' }"
      />
      <a-pagination
        class="task-drawer-pagination"
        show-size-changer
        show-quick-jumper
        :current="query.pageNum"
        :total="total"
        :page-size="query.pageSize"
        :showTotal="total => `共 ${total} 条`"
        @showSizeChange="onShowSizeChange"
        @change="onPageChange"
      />
    </div>
  </a-drawer>
</template>

<script>
import moment from 'moment'
import { taskPage, taskUserOptions } from '@/api/system/dept'

export default {
  name: 'TaskDetailDrawer',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    projectId: {
      type: [Number, String],
      default: null
    },
    projectName: {
      type: String,
      default: ''
    },
    // 形如 ['YYYY-MM-DD','YYYY-MM-DD']，为空则不传时间参数
    dateRange: {
      type: Array,
      default: () => []
    },
    taskStatus: {
      type: [Number, String],
      default: null
    }
  },
  data () {
    return {
      loading: false,
      exportLoading: false,
      list: [],
      total: 0,
      userOptions: [],
      query: {
        pageNum: 1,
        pageSize: 10
      },
      filters: {
        dateRange: [],
        taskStatus: null,
        userIds: []
      },
      columns: [
        {
          title: '序号',
          width: 60,
          align: 'center',
          customRender: (text, record, index) => (this.query.pageNum - 1) * this.query.pageSize + index + 1
        },
        { title: '任务名', dataIndex: 'spaceName', width: 160 },
        { title: '员工', dataIndex: 'nickname', width: 120 },
        { title: '所需时间', dataIndex: 'serviceMinutes', width: 130 },
        { title: '当前进度', dataIndex: 'taskStatusLabel', width: 100 },
        { title: '任务时间', dataIndex: 'taskTime', width: 170 }
      ]
    }
  },

  watch: {
    visible (val) {
      if (val) {
        this.query.pageNum = 1
        // 打开时把父级传入的日期区间作为默认值
        this.filters.dateRange = Array.isArray(this.dateRange) ? this.dateRange.slice(0, 2) : []
        this.filters.taskStatus = (this.taskStatus !== undefined && this.taskStatus !== null && this.taskStatus !== '')
          ? Number(this.taskStatus)
          : null
        this.filters.userIds = []
        this.fetchUserOptions()
        this.fetchList()
      }
    },
    projectId () {
      if (this.visible) {
        this.query.pageNum = 1
        this.filters.userIds = []
        this.fetchUserOptions()
        this.fetchList()
      }
    },
    dateRange () {
      if (this.visible) {
        // 父级日期变化时，同步到筛选条件（不自动触发查询，避免频繁刷新）
        this.filters.dateRange = Array.isArray(this.dateRange) ? this.dateRange.slice(0, 2) : []
      }
    }
  },
  methods: {
    formatRate (rate) {
      const n = rate == null || rate === '' ? 0 : Number(rate)
      return `${Number.isNaN(n) ? 0 : n}%`
    },
    /** 禁用今天之后的日期，仅允许选今天及之前 */
    disabledDateAfterToday (current) {
      if (!current) return false
      return current.isAfter(moment(), 'day')
    },
    handleClose () {
      this.$emit('update:visible', false)
    },
    onPageChange (page) {
      this.query.pageNum = page
      this.fetchList()
    },
    onShowSizeChange (current, size) {
      this.query.pageNum = 1
      this.query.pageSize = size
      this.fetchList()
    },
    onFilterChange () {
      // 仅调整筛选条件时先回到第一页
      this.query.pageNum = 1
    },
    onDateRangeChange () {
      this.query.pageNum = 1
      // 时间变化后刷新人员完成率选项，并清空已选人员（避免选项与完成率口径不一致）
      this.filters.userIds = []
      this.fetchUserOptions()
    },
    handleQuery () {
      this.query.pageNum = 1
      this.fetchUserOptions()
      this.fetchList()
    },
    resetQuery () {
      this.filters.dateRange = []
      this.filters.taskStatus = null
      this.filters.userIds = []
      this.query.pageNum = 1
      this.fetchUserOptions()
      this.fetchList()
    },
    buildQueryParams () {
      const params = {
        projectId: this.projectId
      }
      if (this.filters.taskStatus !== null && this.filters.taskStatus !== undefined && this.filters.taskStatus !== '') {
        params.taskStatus = this.filters.taskStatus
      }
      if (this.filters.dateRange && this.filters.dateRange.length === 2) {
        params.startTime = this.filters.dateRange[0]
        params.endTime = this.filters.dateRange[1]
      }
      if (this.filters.userIds && this.filters.userIds.length > 0) {
        params.userIds = this.filters.userIds
      }
      return params
    },
    fetchUserOptions () {
      if (!this.projectId) {
        this.userOptions = []
        return
      }
      const params = {
        projectId: this.projectId
      }
      if (this.filters.dateRange && this.filters.dateRange.length === 2) {
        params.startTime = this.filters.dateRange[0]
        params.endTime = this.filters.dateRange[1]
      }
      taskUserOptions(params).then(res => {
        this.userOptions = (res && res.data) ? res.data : []
      }).catch(() => {
        this.userOptions = []
      })
    },
    fetchList () {
      if (!this.projectId) {
        this.list = []
        this.total = 0
        return
      }
      const params = {
        ...this.buildQueryParams(),
        pageNum: this.query.pageNum,
        pageSize: this.query.pageSize
      }
      this.loading = true
      taskPage(params).then(res => {
        const data = res && res.data ? res.data : null
        this.list = (data && data.records) ? data.records : []
        this.total = (data && data.total) ? data.total : 0
      }).catch(() => {
        this.$message.error('获取任务详情失败', 3)
      }).finally(() => {
        this.loading = false
      })
    },
    handleExport () {
      if (!this.projectId) {
        this.$message.warning('请先选择项目')
        return
      }
      const that = this
      this.$confirm({
        title: '是否确认导出?',
        content: '将导出当前筛选条件下的全部任务详情',
        onOk () {
          that.exportLoading = true
          return that.download(
            'system/dept/taskExport',
            that.buildQueryParams(),
            `任务详情_${that.projectName || '项目'}_${new Date().getTime()}.xlsx`
          ).finally(() => {
            that.exportLoading = false
          })
        },
        onCancel () {}
      })
    }
  }
}
</script>

<style scoped>
.task-drawer-filters {
  margin-bottom: 16px;
  padding: 14px 16px;
  background: #fafafa;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
}

.task-filter-form {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0 8px;
}

.task-filter-form :deep(.ant-form-item) {
  margin-bottom: 0;
  margin-right: 16px;
}

.task-filter-form :deep(.ant-form-item-label) {
  width: 72px;
  line-height: 32px;
  padding-right: 8px;
}

.task-filter-form :deep(.ant-form-item-control-wrapper) {
  flex: 0 0 auto;
}

.filter-range-picker {
  width: 240px;
}

.filter-user-select {
  min-width: 260px;
  max-width: 360px;
}

.user-option {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  gap: 12px;
}

.user-option-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-option-rate {
  color: #1890ff;
  flex-shrink: 0;
}

.filter-status-select {
  width: 120px;
}

.task-filter-btns {
  margin-left: auto;
  display: inline-flex;
  gap: 8px;
}

.task-drawer-body :deep(.ant-table) {
  margin-bottom: 12px;
}

.task-drawer-pagination {
  display: flex;
  justify-content: flex-end;
  margin-top: 8px;
}
</style>
